"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

import { Button } from "@/lib/components/button";
import { useFetchClient } from "@/lib/fetch-client";

import type { Route } from "next";

export function DashboardLink() {
	const router = useRouter();
	const fetchClient = useFetchClient();
	const [dashboardUrl, setDashboardUrl] = useState<Route>(
		"/dashboard" as Route,
	);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		async function getDashboardUrl() {
			try {
				// Fetch organizations
				const { data: orgsData } = await fetchClient.GET("/orgs", {});

				if (!orgsData?.organizations || orgsData.organizations.length === 0) {
					setDashboardUrl("/onboarding" as Route);
					setIsLoading(false);
					return;
				}

				const defaultOrgId = orgsData.organizations[0].id;

				// Fetch projects for the first organization
				const { data: projectsData } = await fetchClient.GET(
					"/orgs/{id}/projects",
					{
						params: {
							path: {
								id: defaultOrgId,
							},
						},
					},
				);

				if (!projectsData?.projects || projectsData.projects.length === 0) {
					setDashboardUrl(`/dashboard/${defaultOrgId}` as Route);
					setIsLoading(false);
					return;
				}

				const defaultProjectId = projectsData.projects[0].id;
				setDashboardUrl(
					`/dashboard/${defaultOrgId}/${defaultProjectId}` as Route,
				);
			} catch (error) {
				console.error("Failed to fetch dashboard URL:", error);
				setDashboardUrl("/dashboard" as Route);
			} finally {
				setIsLoading(false);
			}
		}

		getDashboardUrl();
	}, [fetchClient]);

	const handleClick = (e: React.MouseEvent) => {
		e.preventDefault();
		router.push(dashboardUrl);
	};

	return (
		<Button size="sm" asChild>
			<Link href={dashboardUrl} onClick={handleClick}>
				{isLoading ? "Loading..." : "Dashboard"}
			</Link>
		</Button>
	);
}
